import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog-sticky-footer';
import { Images, Infinity, Zap } from 'lucide-react';
import * as React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AnimatedImageGrid } from '@/components/ui/animated-image-grid';
import PicStudioDriveLogo from '@components/picstudio-drive-logo';
import { IconContainer } from '@/components/ui/icon-container';
import { logos, MiniLogoCloud } from '@components/Marketing/LogoCloud/LogoCloud';
import { PICSTUDIO_DRIVE_PRODUCT_NAME, slugify, getProductsByType } from '@acme/lib';
import { useTrackClientEvent } from '@analytics/trackClientEvent';
import { useState } from 'react';
import { Loader2 } from 'lucide-react';

interface PicStudioDriveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubscribe?: () => void;
  isLoading?: boolean;
  returnUrl?: string;
}

export function PicStudioDriveDialog({
  open,
  onOpenChange,
  onSubscribe,
  isLoading: externalIsLoading,
  returnUrl = '/dashboard'
}: PicStudioDriveDialogProps) {
  const [isYearly, setIsYearly] = useState(false); // Default to monthly billing
  const router = useRouter();
  const { trackEvent } = useTrackClientEvent();
  const [isLoading, setIsLoading] = useState(false);

  // Get subscription products
  const subscriptionProducts = getProductsByType('subscription');

  // Find monthly and yearly plans
  const monthlyPlan = subscriptionProducts?.find(p => p.productName === PICSTUDIO_DRIVE_PRODUCT_NAME);

  const yearlyPlan = subscriptionProducts?.find(p => p.productName === `${PICSTUDIO_DRIVE_PRODUCT_NAME} (Yearly)`);

  // Get the selected plan based on state
  const selectedPlan = isYearly ? yearlyPlan : monthlyPlan;

  // Format price for display
  const formattedPrice = isYearly ? selectedPlan?.price?.toFixed(0) || selectedPlan?.price?.toFixed(2) : '9.99';
  const billingPeriod = isYearly ? 'year' : 'month';

  const handleSubscribe = async () => {
    if (onSubscribe) {
      onSubscribe();
      return;
    }

    setIsLoading(true);
    try {
      trackEvent('initiateCheckout', {
        value: 'PicStudio Drive',
        metadata: {
          productName: 'PicStudio Drive',
          itemCount: 1
        }
      });

      const searchParams = new URLSearchParams({
        quantity: '1',
        returnUrl,
        interval: isYearly ? 'yearly' : 'monthly'
      });
      router.push(`/payment/subscription/checkout/${slugify(PICSTUDIO_DRIVE_PRODUCT_NAME)}?${searchParams.toString()}`);
      // close the dialog
      onOpenChange(false);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Use external isLoading state if provided, otherwise use internal state
  const buttonIsLoading = externalIsLoading !== undefined ? externalIsLoading : isLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-none flex flex-col gap-0 p-0 md:h-auto sm:max-w-screen-lg">
        <div className="flex overflow-hidden h-fit">
          {/* Left side - Animated Image Grid */}
          <div className="hidden md:block w-1/2 h-auto relative overflow-hidden">
            <AnimatedImageGrid
              columnCount={3}
              className="absolute inset-0 scale-110 flex gap-2 bg-black"
              imageClassName="w-full aspect-[2/3] rounded-md object-cover"
              opacity="opacity-100"
              columnVisibilityClasses={['', '', 'hidden lg:block']}
            />
            {/* Add a gradient overlay for better text readability */}
            <div className="absolute inset-0 bg-gradient-to-r from-black to-black/50 z-10 opacity-50" />
          </div>

          {/* Right side - Content */}
          <div className="w-full md:w-1/2 flex flex-col sm:max-h-[640px] sm:h-screen">
            <div className="overflow-y-auto px-8 pt-12 pb-4 h-full">
              {/* Tabs */}
              <div className="mb-8">
                <Tabs
                  defaultValue="Monthly"
                  value={isYearly ? 'Yearly' : 'Monthly'}
                  onValueChange={value => setIsYearly(value === 'Yearly')}
                >
                  <TabsList className="grid w-[200px] grid-cols-2 bg-foreground/10 mx-auto ">
                    <TabsTrigger value="Monthly">Monthly</TabsTrigger>
                    <TabsTrigger value="Yearly">Yearly</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              <DialogHeader className="mb-6">
                <DialogTitle className="text-4xl font-medium">
                  Upgrade to <PicStudioDriveLogo className="w-72 mx-auto sm:mx-0" />
                </DialogTitle>
                <DialogDescription className="text-lg text-foreground">
                  Stay up to date with the latest AI models and get 80 photos delivered monthly.
                  <span>
                    {' '}
                    <Link href="/drive" className="text-accent">
                      Learn more
                    </Link>
                  </span>
                </DialogDescription>
              </DialogHeader>

              {/* Features List - Updated with content from drive/index.tsx */}
              <div className="space-y-8">
                {/* Monthly Perks Section */}
                <div className="p-6 bg-accent/10 rounded-lg border border-accent/20">
                  <div className="space-y-4 text-foreground text-sm">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 mt-1">
                        <IconContainer borderless size="sm" className="bg-gradient-to-tl from-pink-400 to-purple-600">
                          <Images size={16} />
                        </IconContainer>
                      </div>
                      <div>
                        <span className="font-bold">Receive two photo packs</span> each month which include one photo
                        pack and a monthly mix
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 mt-1">
                        <IconContainer borderless size="sm" className="bg-gradient-to-tl from-teal-400 to-blue-600">
                          <Zap size={16} />
                        </IconContainer>
                      </div>

                      <div>
                        <span className="font-bold">Free model upgrades</span> keep your AI model current
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 mt-1">
                        <IconContainer borderless size="sm" className="bg-gradient-to-tl from-orange-400 to-orange-600">
                          <Infinity size={16} />
                        </IconContainer>
                      </div>
                      <div>
                        <span className="font-bold">Unlimited storage</span> secures all of your models and photos
                      </div>
                    </div>
                  </div>
                </div>
                <MiniLogoCloud
                  textClassName="text-xs text-muted-foreground mb-2"
                  logos={logos}
                  maxLogos={4}
                  mobileColumns={4}
                  desktopColumns={4}
                  gap="xs"
                  className="px-10 dark:invert"
                />
              </div>
            </div>

            <DialogFooter className="p-8 flex flex-col items-center">
              <div className="w-full space-y-4">
                <Button
                  size="lg"
                  variant="accent"
                  onClick={handleSubscribe}
                  disabled={buttonIsLoading}
                  className="w-full"
                  data-product-name="PicStudio Drive"
                  data-price={formattedPrice}
                  data-subscription="true"
                >
                  {buttonIsLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Upgrade for just ${formattedPrice}/{billingPeriod}
                </Button>
                <p className="text-xs text-muted-foreground text-center">Cancel your subscription anytime</p>
              </div>
            </DialogFooter>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
